import { supabase } from "@/integrations/supabase/client";
import { Tag, RecordTag, TAG_COLORS, TagColor, Tables } from "@/integrations/supabase/types";

// ==================== TYPES ====================

export interface CreateTagInput {
  name: string;
  color?: string;
  sort_order?: number;
}

export interface UpdateTagInput {
  name?: string;
  color?: string;
  sort_order?: number;
  deleted_at?: string | null;
}

export interface TagOperationResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

// ==================== TAG CRUD OPERATIONS ====================

/**
 * Получить все теги пользователя (исключая удаленные)
 */
export const getUserTags = async (userId: string): Promise<Tag[]> => {
  const { data, error } = await supabase
    .from("tags")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching user tags:", error);
    throw error;
  }

  return data || [];
};

/**
 * Создать новый тег
 */
export const createTag = async (
  userId: string, 
  tagInput: CreateTagInput
): Promise<Tag> => {
  const trimmedName = tagInput.name.trim();

  // Проверяем существование тега (включая удаленные)
  const { data: existingTag } = await supabase
    .from("tags")
    .select("id, deleted_at, color, sort_order")
    .eq("user_id", userId)
    .eq("name", trimmedName)
    .maybeSingle();

  // Если тег существует и НЕ удален - ошибка
  if (existingTag && !existingTag.deleted_at) {
    throw new Error(`Тег с названием "${tagInput.name}" уже существует`);
  }

  // Если тег существует, но удален - восстанавливаем его
  if (existingTag && existingTag.deleted_at) {
    const updatedTag = await updateTag(existingTag.id, {
      color: tagInput.color || existingTag.color,
      sort_order: tagInput.sort_order || existingTag.sort_order,
      deleted_at: null, // восстанавливаем тег
    });
    return updatedTag;
  }

  // Получаем следующий sort_order если не указан
  let sortOrder = tagInput.sort_order ?? 0;
  if (sortOrder === 0) {
    const { data: maxSortTag } = await supabase
      .from("tags")
      .select("sort_order")
      .eq("user_id", userId)
      .is("deleted_at", null)
      .order("sort_order", { ascending: false })
      .limit(1)
      .maybeSingle();

    sortOrder = (maxSortTag?.sort_order ?? 0) + 1;
  }

  // Создаем новый тег
  const { data, error } = await supabase
    .from("tags")
    .insert({
      user_id: userId,
      name: trimmedName,
      color: tagInput.color || getRandomTagColor(),
      sort_order: sortOrder,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating tag:", error);
    throw error;
  }

  return data;
};

/**
 * Обновить тег
 */
export const updateTag = async (
  tagId: string,
  updates: UpdateTagInput
): Promise<Tag> => {
  // Если обновляется имя, проверяем уникальность
  if (updates.name && updates.deleted_at === null) {
    const { data: tag } = await supabase
      .from("tags")
      .select("user_id")
      .eq("id", tagId)
      .single();

    if (tag) {
      const { data: existingTag } = await supabase
        .from("tags")
        .select("id")
        .eq("user_id", tag.user_id)
        .eq("name", updates.name.trim())
        .neq("id", tagId)
        .is("deleted_at", null)
        .maybeSingle();

      if (existingTag) {
        throw new Error(`Тег с названием "${updates.name}" уже существует`);
      }
    }
  }

  const cleanUpdates: Record<string, unknown> = {};
  if (updates.name) cleanUpdates.name = updates.name.trim();
  if (updates.color) cleanUpdates.color = updates.color;
  if (updates.sort_order !== undefined) cleanUpdates.sort_order = updates.sort_order;
  if (updates.deleted_at !== undefined) cleanUpdates.deleted_at = updates.deleted_at;

  const { data, error } = await supabase
    .from("tags")
    .update(cleanUpdates)
    .eq("id", tagId)
    .select()
    .single();

  if (error) {
    console.error("Error updating tag:", error);
    throw error;
  }

  return data;
};

/**
 * Мягкое удаление тега
 */
export const deleteTag = async (tagId: string): Promise<void> => {
  const { error } = await supabase.rpc("soft_delete_tag", {
    tag_id_param: tagId,
  });

  if (error) {
    console.error("Error deleting tag:", error);
    throw error;
  }
};

/**
 * Восстановить удаленный тег
 */
export const restoreTag = async (tagId: string): Promise<void> => {
  const { error } = await supabase.rpc("restore_tag", {
    tag_id_param: tagId,
  });

  if (error) {
    console.error("Error restoring tag:", error);
    throw error;
  }
};

/**
 * Получить удаленные теги пользователя
 */
export const getDeletedTags = async (userId: string): Promise<Tag[]> => {
  const { data, error } = await supabase
    .from("tags")
    .select("*")
    .eq("user_id", userId)
    .not("deleted_at", "is", null)
    .order("deleted_at", { ascending: false });

  if (error) {
    console.error("Error fetching deleted tags:", error);
    throw error;
  }

  return data || [];
};

/**
 * Обновить порядок тегов пользователя
 */
export const reorderTags = async (
  userId: string, 
  tagIds: string[]
): Promise<void> => {
  const tagOrder: Record<string, number> = {};
  tagIds.forEach((id, index) => {
    tagOrder[id] = index;
  });

  const { error } = await supabase.rpc("update_tags_order", {
    user_id_param: userId,
    tag_order: tagOrder,
  });

  if (error) {
    console.error("Error reordering tags:", error);
    throw error;
  }
};

// ==================== RECORD-TAG OPERATIONS ====================

/**
 * Получить теги записи
 */
export const getRecordTags = async (recordId: string): Promise<Tag[]> => {
  const { data, error } = await supabase
    .from("record_tags")
    .select(`
      tags (*)
    `)
    .eq("record_id", recordId);

  if (error) {
    console.error("Error fetching record tags:", error);
    throw error;
  }

  return (data || [])
    .map(item => item.tags)
    .filter(Boolean) as Tag[];
};

/**
 * Добавить теги к записи
 */
export const addTagsToRecord = async (
  recordId: string, 
  tagIds: string[]
): Promise<void> => {
  // Создаем записи для связи, игнорируя дубликаты
  const recordTags = tagIds.map(tagId => ({
    record_id: recordId,
    tag_id: tagId,
  }));

  const { error } = await supabase
    .from("record_tags")
    .upsert(recordTags, { 
      onConflict: "record_id,tag_id",
      ignoreDuplicates: true 
    });

  if (error) {
    console.error("Error adding tags to record:", error);
    throw error;
  }
};

/**
 * Удалить теги из записи
 */
export const removeTagsFromRecord = async (
  recordId: string, 
  tagIds: string[]
): Promise<void> => {
  const { error } = await supabase
    .from("record_tags")
    .delete()
    .eq("record_id", recordId)
    .in("tag_id", tagIds);

  if (error) {
    console.error("Error removing tags from record:", error);
    throw error;
  }
};

/**
 * Обновить теги записи (полная замена)
 */
export const updateRecordTags = async (
  recordId: string, 
  tagIds: string[]
): Promise<void> => {
  // Сначала удаляем все существующие связи
  const { error: deleteError } = await supabase
    .from("record_tags")
    .delete()
    .eq("record_id", recordId);

  if (deleteError) {
    console.error("Error removing existing record tags:", deleteError);
    throw deleteError;
  }

  // Затем добавляем новые связи, если есть теги
  if (tagIds.length > 0) {
    await addTagsToRecord(recordId, tagIds);
  }
};

/**
 * Получить записи с определенными тегами
 */
export const getRecordsByTags = async (
  userId: string,
  tagIds: string[]
): Promise<string[]> => {
  const { data, error } = await supabase
    .from("record_tags")
    .select(`
      record_id,
      records!inner (
        user_id
      )
    `)
    .in("tag_id", tagIds)
    .eq("records.user_id", userId);

  if (error) {
    console.error("Error fetching records by tags:", error);
    throw error;
  }

  return Array.from(
    new Set((data || []).map(item => item.record_id))
  );
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Получить случайный цвет тега
 */
export const getRandomTagColor = (): TagColor => {
  const randomIndex = Math.floor(Math.random() * TAG_COLORS.length);
  return TAG_COLORS[randomIndex];
};

/**
 * Проверить валидность цвета тега
 * Поддерживает как предустановленные цвета, так и произвольные hex цвета
 */
export const isValidTagColor = (color: string): boolean => {
  // Проверяем предустановленные цвета
  if ((TAG_COLORS as readonly string[]).includes(color)) {
    return true;
  }
  
  // Проверяем hex формат
  const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;
  return hexRegex.test(color);
};

/**
 * Получить статистику использования тегов
 */
export const getTagUsageStats = async (userId: string): Promise<Array<{
  tag: Tag;
  usage_count: number;
}>> => {
  const { data, error } = await supabase
    .from("tags")
    .select(`
      *,
      record_tags (
        count
      )
    `)
    .eq("user_id", userId)
    .is("deleted_at", null);

  if (error) {
    console.error("Error fetching tag usage stats:", error);
    throw error;
  }

  return (data || []).map(tag => ({
    tag,
    usage_count: tag.record_tags?.length || 0,
  }));
};

/**
 * Поиск тегов по названию
 */
export const searchTags = async (
  userId: string, 
  query: string, 
  limit: number = 10
): Promise<Tag[]> => {
  if (!query.trim()) {
    return getUserTags(userId);
  }

  const { data, error } = await supabase
    .from("tags")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .ilike("name", `%${query.trim()}%`)
    .order("sort_order", { ascending: true })
    .limit(limit);

  if (error) {
    console.error("Error searching tags:", error);
    throw error;
  }

  return data || [];
};

// ==================== BATCH OPERATIONS ====================

/**
 * Массовое создание тегов
 */
export const createTagsBatch = async (
  userId: string,
  tagInputs: CreateTagInput[]
): Promise<Tag[]> => {
  // Получаем максимальный sort_order
  const { data: maxSortTag } = await supabase
    .from("tags")
    .select("sort_order")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("sort_order", { ascending: false })
    .limit(1)
    .maybeSingle();

  let nextSortOrder = (maxSortTag?.sort_order ?? 0) + 1;

  const tagsToCreate = tagInputs.map(input => ({
    user_id: userId,
    name: input.name.trim(),
    color: input.color || getRandomTagColor(),
    sort_order: input.sort_order ?? nextSortOrder++,
  }));

  const { data, error } = await supabase
    .from("tags")
    .insert(tagsToCreate)
    .select();

  if (error) {
    console.error("Error creating tags batch:", error);
    throw error;
  }

  return data || [];
};

/**
 * Массовое удаление тегов
 */
export const deleteTagsBatch = async (tagIds: string[]): Promise<void> => {
  const { error } = await supabase
    .from("tags")
    .update({ deleted_at: new Date().toISOString() })
    .in("id", tagIds);

  if (error) {
    console.error("Error deleting tags batch:", error);
    throw error;
  }
};

export default {
  // CRUD операции
  getUserTags,
  createTag,
  updateTag,
  deleteTag,
  restoreTag,
  getDeletedTags,
  reorderTags,
  
  // Операции с записями
  getRecordTags,
  addTagsToRecord,
  removeTagsFromRecord,
  updateRecordTags,
  getRecordsByTags,
  
  // Утилиты
  getRandomTagColor,
  isValidTagColor,
  getTagUsageStats,
  searchTags,
  
  // Batch операции
  createTagsBatch,
  deleteTagsBatch,
};